// sales.component.ts
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbCalendar, NgbDate, NgbDateStruct, NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ReportingService } from '../_service/reporting.service';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, transition, animate } from '@angular/animations';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface SalesSummary {
  total_payments: any;
  pending_payments: any;
  completed_payments: any;
}


export interface PaymentReportHistory {
  order_id: string;
  order_uid: string | null;
  order_amount: any;
  payment_method: any;
  status: string;
  transaction_date: string | null;
}

export interface MonicreditTransaction {
  id: number;
  reference: string;
  settlement_amount: string;
  amount_paid: string;
  source_account_number: string;
  source_account_name: string;
  source_bank_name: string;
  destination_account_number: string;
  transaction_type: string;
  created_at: string;
}

export interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  path: string;
  per_page: number;
  to: number;
  total: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: PaginationMeta;
}
export interface SalesHistory {
  order_id: number;
  order_uid: string;
  order_amount: string;
  payment_method: string[];
  status: string;
  transaction_date: string;
}

export interface SalesHistoryParams {
  page?: number;
  start_date?: string;
  end_date?: string;
  payment_method?: string;
  waiter_id?: string;
}
@Component({
  selector: 'app-financial-report',
  standalone: true,
  imports: [NgbModule, RouterModule, CommonModule, FormsModule],
  templateUrl: './financial-report.component.html',
  styleUrl: './financial-report.component.scss',
  animations: [
    trigger('slideDown', [
      state('collapsed', style({
        height: '0px',
        overflow: 'hidden',
        opacity: 0
      })),
      state('expanded', style({
        height: '*',
        overflow: 'visible',
        opacity: 1
      })),
      transition('collapsed <=> expanded', [
        animate('300ms ease-in-out')
      ])
    ])
  ]
})
export class FinancialReportComponent {
  private reportingSrvs = inject(ReportingService);
  private modal = inject(NgbModal);
  private calendar = inject(NgbCalendar);

  active = 1;
  paymentHistory: PaymentReportHistory[] = [];
  monicreditTransactions: MonicreditTransaction[] = [];
  page = 1;
  pageSize = 15;
  size = 0;
  salesHistory: SalesHistory[] = [];
  filteredsalesHistory: SalesHistory[] = [];
  historyPage = 1;
  historyPageSize = 15;
  historyTotal = 0;
  searchTerm: string = '';
  dateRange: { from: NgbDate | null; to: NgbDate | null } = {
    from: null,
    to: null
  };
  hoveredDate: NgbDate | null = null;
  selectedPaymentMethod: string = '';
  selectedWaiter: string = '';
  showDatePicker = false;
  isLoading = false;
  isFiltersCollapsed = true; // Filters collapsed by default
  isExporting = false; // Loading state for export operations
  isLoadingSummary = false; // Loading state for summary data

  // Filter properties
  filterParams = {
    start_date: '',
    end_date: '',
    payment_method: '',
    waiter_id: ''
  };

  // Properties
  totalSales: SalesSummary = {
    total_payments: 0,
    pending_payments: 0,
    completed_payments: 0
  };

  summaryCards = [
    { title: 'Total Transactions', value: 0, icon: 'fas fa-exchange-alt', color: 'primary' },
    { title: 'Total Amount Settled', value: 0, icon: 'fas fa-money-bill-wave', color: 'success' },
    { title: 'Total Amount Paid', value: 0, icon: 'fas fa-credit-card', color: 'info' }
  ];
  walletBalance: any;

  // Toggle filters collapse state
  toggleFiltersCollapse(): void {
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
  }


  ngOnInit(): void {
    this.getMonicreditTransactions();
    this.getPaymentSummary();
    this.getWalletBalance();
    // Don't set default date range to allow showing all data initially
  }

  getMonicreditTransactions(page: number = 1) {
    this.isLoading = true;

    this.reportingSrvs.getMonicreditTransactions(page).subscribe({
      next: (response) => {
        // Map monicredit transactions to payment history format for display
        this.paymentHistory = response.data.map((transaction: MonicreditTransaction) => ({
          order_id: transaction.id.toString(),
          order_uid: transaction.reference,
          order_amount: transaction.settlement_amount,
          payment_method: [transaction.transaction_type],
          status: 'completed', // Assuming all monicredit transactions are completed
          transaction_date: transaction.created_at
        }));

        this.monicreditTransactions = response.data;
        this.size = response.meta.total;
        this.page = response.meta.current_page;

        // Update summary cards with transaction data
        this.updateTransactionSummary();

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching monicredit transactions:', error);
        this.isLoading = false;
      }
    });
  }

  updateTransactionSummary() {
    const totalTransactions = this.size;
    // const totalSettled = this.monicreditTransactions.reduce((sum, tx) => sum + parseFloat(tx.settlement_amount), 0);
    // const totalPaid = this.monicreditTransactions.reduce((sum, tx) => sum + parseFloat(tx.amount_paid), 0);

    this.summaryCards = [
      { title: 'Total Transactions', value: totalTransactions, icon: 'fas fa-exchange-alt', color: 'primary' },
      // { title: 'Total Amount Settled', value: totalSettled, icon: 'fas fa-money-bill-wave', color: 'success' },
      // { title: 'Total Amount Paid', value: totalPaid, icon: 'fas fa-credit-card', color: 'info' }
    ];
  }

  getPaymentHistory(page: number = 1) {
    this.isLoading = true;

    // Use the original payment history method first, then we'll enhance it
    this.reportingSrvs.getPaymentHistory(page).subscribe({
      next: (response) => {
        this.paymentHistory = response.data;
        this.size = response.meta.total;
        this.page = response.meta.current_page;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching payment history:', error);
        this.isLoading = false;
      }
    });
  }

  getFilteredPaymentHistory(page: number = 1) {
    this.isLoading = true;
    const params = this.buildFilterParams(page);

    this.reportingSrvs.getSalesHistory(params).subscribe({
      next: (response) => {
        // Convert SalesHistory to PaymentReportHistory format
        this.paymentHistory = response.data.map(item => ({
          order_id: item.order_id.toString(),
          order_uid: item.order_uid,
          order_amount: item.order_amount,
          payment_method: item.payment_method,
          status: item.status,
          transaction_date: item.transaction_date
        }));
        this.size = response.meta.total;
        this.page = response.meta.current_page;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching filtered payment history:', error);
        this.isLoading = false;
      }
    });
  }

  buildFilterParams(page: number = 1) {
    const params: any = { page };

    if (this.dateRange.from) {
      params.start_date = this.formatDate(this.dateRange.from);
    }
    if (this.dateRange.to) {
      params.end_date = this.formatDate(this.dateRange.to);
    }
    if (this.selectedPaymentMethod) {
      params.payment_method = this.selectedPaymentMethod;
    }
    if (this.selectedWaiter) {
      params.waiter_id = this.selectedWaiter;
    }

    return params;
  }

  formatDate(date: NgbDate): string {
    return `${date.year}-${date.month.toString().padStart(2, '0')}-${date.day.toString().padStart(2, '0')}`;
  }

  // Date picker methods
  onDateSelection(date: NgbDate) {
    if (!this.dateRange.from && !this.dateRange.to) {
      this.dateRange.from = date;
    } else if (this.dateRange.from && !this.dateRange.to && date.after(this.dateRange.from)) {
      this.dateRange.to = date;
      this.showDatePicker = false;
      this.applyFilters();
    } else {
      this.dateRange.to = null;
      this.dateRange.from = date;
    }
  }

  isHovered(date: NgbDate) {
    return this.dateRange.from && !this.dateRange.to && this.hoveredDate && date.after(this.dateRange.from) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return this.dateRange.to && date.after(this.dateRange.from) && date.before(this.dateRange.to);
  }

  isRange(date: NgbDate) {
    return date.equals(this.dateRange.from) || (this.dateRange.to && date.equals(this.dateRange.to)) || this.isInside(date) || this.isHovered(date);
  }

  applyFilters() {
    this.page = 1;
    // For now, we'll just reload the monicredit transactions
    // Later we can implement filtering on the backend
    this.getMonicreditTransactions(1);
  }

  hasActiveFilters(): boolean {
    return !!(this.dateRange.from || this.dateRange.to || this.selectedPaymentMethod || this.selectedWaiter || this.searchTerm);
  }

  // Search functionality
  onSearchChange() {
    // Debounce search to avoid too many API calls
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applyFilters();
    }, 500);
  }

  private searchTimeout: any;

  refresh(page: number) {
    this.getMonicreditTransactions(page);
  }

  onPaymentMethodChange() {
    this.applyFilters();
  }

  onWaiterChange() {
    this.applyFilters();
  }

  clearAllFilters() {
    this.dateRange = { from: null, to: null };
    this.selectedPaymentMethod = '';
    this.selectedWaiter = '';
    this.searchTerm = '';
    this.getMonicreditTransactions(1);
  }
  getPaymentSummary() {
    this.isLoadingSummary = true;
    this.reportingSrvs.getPaymentSummary().subscribe({
      next: (data: any) => {
        this.summaryCards = [
          { title: 'Total Payments', value: data.total_payments, icon: 'fas fa-money-bill-wave', color: 'primary' },
          { title: 'Pending Payments', value: parseInt(data.pending_payments), icon: 'fas fa-clock', color: 'warning' },
          { title: 'Completed Payments', value: data.completed_payments, icon: 'fas fa-check-circle', color: 'success' }
        ];
        this.isLoadingSummary = false;
      },
      error: (error) => {
        console.error('Error fetching sales summary:', error);
        this.isLoadingSummary = false;
      }
    });
  }

  getWalletBalance() {
    this.reportingSrvs.getWalletBalance().subscribe({
      next: (res: any) => {
        this.walletBalance = res;
      },
      error: (error) => {
        console.error('Error fetching watlle balance:', error);
      }
    });
  }

  updateSummaryCards() {
    this.summaryCards = [
      { title: 'Total Sales', value: this.totalSales.total_payments, icon: 'fas fa-money-bill-wave', color: 'primary' },
      { title: 'Total Deductions', value: this.totalSales.pending_payments, icon: 'fas fa-minus-circle', color: 'danger' },
      { title: 'Completed Payments', value: this.totalSales.completed_payments, icon: 'fas fa-check-circle', color: 'success' }
    ];
  }

  // Utility methods for template
  trackByOrderId(index: number, item: PaymentReportHistory): string {
    return item.order_id;
  }

  trackByTransactionId(index: number, item: MonicreditTransaction): number {
    return item.id;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'badge bg-success';
      case 'closed':
        return 'badge bg-danger';
      case 'pending':
        return 'badge bg-warning';
      default:
        return 'badge bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed':
        return 'Paid';
      case 'closed':
        return 'Closed';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'completed':
        return 'fas fa-check-circle';
      case 'closed':
        return 'fas fa-times-circle';
      case 'pending':
        return 'fas fa-clock';
      default:
        return 'fas fa-question-circle';
    }
  }

  // Trend calculation methods for KPI cards
  getTrendClass(index: number): string {
    const trends = ['positive', 'negative', 'positive']; // Mock trend data
    return trends[index] || 'neutral';
  }

  getTrendIcon(index: number): string {
    const icons = ['fas fa-arrow-up', 'fas fa-arrow-down', 'fas fa-arrow-up'];
    return icons[index] || 'fas fa-minus';
  }

  getTrendValue(index: number): string {
    const values = ['8.2', '2.1', '12.5']; // Mock trend percentages
    return values[index] || '0.0';
  }

  // Math utility for template
  Math = Math;

  getActiveFiltersCount(): number {
    let count = 0;
    if (this.dateRange.from || this.dateRange.to) count++;
    if (this.selectedPaymentMethod) count++;
    if (this.searchTerm) count++;
    return count;
  }

  getPaymentMethodDisplay(method: string): string {
    const methods: { [key: string]: string } = {
      'cash': '💵 Cash',
      'card': '💳 Card',
      'transfer': '🏦 Bank Transfer',
      'pos': '🏧 POS Terminal'
    };
    return methods[method] || method;
  }

  clearDateFilter(): void {
    this.dateRange = { from: null, to: null };
    this.applyFilters();
  }

  clearPaymentMethod(): void {
    this.selectedPaymentMethod = '';
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.applyFilters();
  }

  exportToExcel(): void {
    this.isExporting = true;
    // Simulate export process
    setTimeout(() => {
      // Export functionality would be implemented here
      console.log('Exporting to Excel...');
      // Here you would call your export service
      // this.reportingSrvs.exportPaymentReport(this.getFilterParams()).subscribe({
      //   next: (response) => {
      //     // Handle successful export
      //     this.isExporting = false;
      //   },
      //   error: (error) => {
      //     console.error('Export failed:', error);
      //     this.isExporting = false;
      //   }
      // });
      this.isExporting = false;
    }, 2000);
  }

}
